import React, { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Copy,
  ExternalLink,
  Info,
  RefreshCw,
  Search,
  XCircle,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { API_BASE_URL, TENANT_ID } from '../utils/api'
import { traceService } from '../services/traceService'
import toast from 'react-hot-toast'

export type DebugLogLevel = 'CRITICAL' | 'ERROR' | 'WARN' | 'INFO' | 'DEBUG'
export type DebugLogSource =
  | 'AI_ROUTER'
  | 'EMBEDDING_ROUTER'
  | 'MESSAGE_PIPELINE'
  | 'WHATSAPP_WEBHOOK'
  | 'QUEUE_WORKER'
  | 'SYSTEM'

export interface UnifiedDebugLog {
  id: string
  source: DebugLogSource
  level: DebugLogLevel
  tenantId: string | null
  tenantName?: string | null
  message: string
  errorCode?: string | null
  stackTrace?: string | null
  payload?: Record<string, unknown> | null
  conversationId?: string | null
  traceId?: string | null
  createdAt: string
  isRead: boolean
  readAt?: string | null
  readBy?: string | null
}

const levelBadgeStyle: Record<DebugLogLevel, { bg: string; text: string; border: string; icon: React.ReactNode }> = {
  CRITICAL: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    icon: <XCircle className="w-3.5 h-3.5 text-red-600 inline mr-1" />,
  },
  ERROR: {
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    border: 'border-rose-200',
    icon: <AlertTriangle className="w-3.5 h-3.5 text-rose-600 inline mr-1" />,
  },
  WARN: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    icon: <AlertTriangle className="w-3.5 h-3.5 text-amber-600 inline mr-1" />,
  },
  INFO: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    icon: <Info className="w-3.5 h-3.5 text-blue-600 inline mr-1" />,
  },
  DEBUG: {
    bg: 'bg-slate-50',
    text: 'text-slate-700',
    border: 'border-slate-200',
    icon: <Info className="w-3.5 h-3.5 text-slate-500 inline mr-1" />,
  },
}

const sourceLabels: Record<DebugLogSource, string> = {
  AI_ROUTER: 'AI Router (LLM)',
  EMBEDDING_ROUTER: 'Embeddings (Vector)',
  MESSAGE_PIPELINE: 'Mensajería / Pipeline',
  WHATSAPP_WEBHOOK: 'WhatsApp Webhooks',
  QUEUE_WORKER: 'Workers / Colas',
  SYSTEM: 'Sistema',
}

export default function DebugLogsPage() {
  const { token } = useAuth()
  const [logs, setLogs] = useState<UnifiedDebugLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [tenants, setTenants] = useState<Array<{ id: string; name: string }>>([])
  const [selectedTenantId, setSelectedTenantId] = useState<string>(TENANT_ID || '')
  const [selectedSource, setSelectedSource] = useState<string>('ALL')
  const [selectedLevel, setSelectedLevel] = useState<string>('ALL')
  const [statusFilter, setStatusFilter] = useState<'all' | 'unread' | 'read'>('unread')
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 })

  // Details modal / expanded
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  // Load tenants list
  useEffect(() => {
    let cancelled = false
    const loadTenants = async () => {
      try {
        const res = await traceService.getTenants()
        if (!cancelled && res.data) {
          setTenants(res.data)
        }
      } catch {
        // Ignorar
      }
    }
    void loadTenants()
    return () => {
      cancelled = true
    }
  }, [])

  // Fetch logs
  const fetchLogs = async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (selectedTenantId) params.append('tenantId', selectedTenantId)
      if (selectedSource !== 'ALL') params.append('source', selectedSource)
      if (selectedLevel !== 'ALL') params.append('level', selectedLevel)
      if (statusFilter !== 'all') params.append('status', statusFilter)
      if (searchTerm.trim()) params.append('search', searchTerm.trim())
      params.append('page', page.toString())
      params.append('limit', '25')

      const res = await fetch(`${API_BASE_URL}/admin/debug-logs?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(selectedTenantId ? { 'x-tenant-id': selectedTenantId } : {}),
        },
      })

      if (!res.ok) {
        throw new Error(`Error ${res.status}: Fallo al cargar logs`)
      }

      const data = await res.json()
      setLogs(data.data || [])
      setPagination(data.pagination || { total: 0, totalPages: 1 })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error desconocido al cargar logs'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void fetchLogs()
  }, [token, selectedTenantId, selectedSource, selectedLevel, statusFilter, page])

  const handleMarkAsRead = async (logId: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/debug-logs/${logId}/read`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...(selectedTenantId ? { 'x-tenant-id': selectedTenantId } : {}),
        },
      })
      if (res.ok) {
        setLogs((prev) =>
          prev.map((l) => (l.id === logId ? { ...l, isRead: true, readAt: new Date().toISOString() } : l))
        )
        toast.success('Log marcado como leído')
        if (statusFilter === 'unread') {
          setPagination((prev) => ({
            ...prev,
            total: Math.max(0, prev.total - 1),
          }))
        }
        window.dispatchEvent(new CustomEvent('debug-logs:updated'))
      } else {
        toast.error('No se pudo marcar el log')
      }
    } catch {
      toast.error('No se pudo marcar el log')
    }
  }

  const handleMarkAllAsRead = async () => {
    const visibleUnreadIds = logs.filter((l) => !l.isRead).map((l) => l.id)
    try {
      setLoading(true)
      const res = await fetch(`${API_BASE_URL}/admin/debug-logs/mark-all-read`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...(selectedTenantId ? { 'x-tenant-id': selectedTenantId } : {}),
        },
        body: JSON.stringify({
          logIds: visibleUnreadIds.length > 0 ? visibleUnreadIds : undefined,
          tenantId: selectedTenantId || undefined,
          source: selectedSource !== 'ALL' ? selectedSource : undefined,
          level: selectedLevel !== 'ALL' ? selectedLevel : undefined,
          search: searchTerm.trim() || undefined,
        }),
      })
      if (res.ok) {
        const data = await res.json().catch(() => ({ markedCount: 0 }))
        const count = data.markedCount ?? visibleUnreadIds.length
        if (count > 0) {
          toast.success(`Se marcaron ${count} logs como leídos`)
        } else {
          toast('No había logs pendientes por marcar')
        }
        // Immediate optimistic UI update
        setLogs((prev) =>
          prev.map((l) => ({ ...l, isRead: true, readAt: new Date().toISOString(), readBy: 'admin' }))
        )
        if (statusFilter === 'unread') {
          setPagination((prev) => ({
            ...prev,
            total: 0,
            totalPages: 1,
          }))
        }
        window.dispatchEvent(new CustomEvent('debug-logs:updated'))
        setPage(1)
        await fetchLogs()
      } else {
        toast.error('Error al marcar logs como leídos')
      }
    } catch {
      toast.error('Error al marcar logs como leídos')
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = (id: string, text: string) => {
    void navigator.clipboard.writeText(text)
    setCopiedId(id)
    toast.success('Copiado al portapapeles')
    setTimeout(() => setCopiedId(null), 2000)
  }

  const unreadCount = useMemo(() => logs.filter((l) => !l.isRead).length, [logs])

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-200 pb-5">
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2">
            <span>🛡️</span> Centro de Logs de Debug y Diagnóstico
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            Monitoreo unificado de fallos en LLM Chat, Embeddings, Webhooks de WhatsApp y Pipelines.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => void fetchLogs()}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>

          <button
            onClick={() => void handleMarkAllAsRead()}
            disabled={(statusFilter === 'unread' ? pagination.total === 0 : unreadCount === 0) || loading}
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-white bg-[#075f55] hover:bg-[#064e46] rounded-xl shadow-sm transition-colors disabled:opacity-40"
            title="Marcar todos los logs no leídos como leídos"
          >
            <Check className="w-3.5 h-3.5" />
            Marcar todos leídos {statusFilter === 'unread' && pagination.total > 0 ? `(${pagination.total})` : unreadCount > 0 ? `(${unreadCount})` : ''}
          </button>
        </div>
      </div>

      {/* Filtros Bar */}
      <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm space-y-4">
        {/* Pestañas de estado de lectura */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-3">
          <div className="flex gap-1.5 bg-gray-100 p-1 rounded-xl">
            {(['unread', 'all', 'read'] as const).map((st) => (
              <button
                key={st}
                onClick={() => {
                  setStatusFilter(st)
                  setPage(1)
                }}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                  statusFilter === st
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                {{
                  unread: `No leídos`,
                  all: 'Todos los logs',
                  read: 'Leídos',
                }[st]}
              </button>
            ))}
          </div>

          <div className="text-xs text-gray-500 font-medium">
            Total registrados: <strong className="text-gray-800">{pagination.total}</strong>
          </div>
        </div>

        {/* Controles de filtrado */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {/* Tenant */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Empresa / Tenant</label>
            <select
              value={selectedTenantId}
              onChange={(e) => {
                setSelectedTenantId(e.target.value)
                setPage(1)
              }}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#075f55] outline-none bg-white"
            >
              <option value="">Todos los Tenants</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.id.slice(0, 8)}...)
                </option>
              ))}
            </select>
          </div>

          {/* Origen */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Módulo / Origen</label>
            <select
              value={selectedSource}
              onChange={(e) => {
                setSelectedSource(e.target.value)
                setPage(1)
              }}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#075f55] outline-none bg-white"
            >
              <option value="ALL">Todos los módulos</option>
              {Object.entries(sourceLabels).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Severidad */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Severidad</label>
            <select
              value={selectedLevel}
              onChange={(e) => {
                setSelectedLevel(e.target.value)
                setPage(1)
              }}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#075f55] outline-none bg-white"
            >
              <option value="ALL">Todas las severidades</option>
              <option value="CRITICAL">CRITICAL</option>
              <option value="ERROR">ERROR</option>
              <option value="WARN">WARN</option>
              <option value="INFO">INFO</option>
            </select>
          </div>

          {/* Búsqueda */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Buscar mensaje o error</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Palabra clave o código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setPage(1)
                    void fetchLogs()
                  }
                }}
                className="w-full pl-8 pr-3 py-2 text-xs border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#075f55] outline-none"
              />
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2.5" />
            </div>
          </div>
        </div>
      </div>

      {/* Estados: Loading, Error, Empty, List */}
      {loading ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center shadow-sm">
          <div className="w-8 h-8 border-3 border-[#075f55] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-xs font-medium text-gray-500">Cargando registros de logs...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center text-red-700">
          <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <h3 className="text-sm font-bold">Error al consultar logs de diagnóstico</h3>
          <p className="text-xs mt-1 text-red-600">{error}</p>
          <button
            onClick={() => void fetchLogs()}
            className="mt-4 px-4 py-1.5 text-xs font-bold text-red-800 bg-red-100 hover:bg-red-200 rounded-xl transition-colors"
          >
            Reintentar
          </button>
        </div>
      ) : logs.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center shadow-sm">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2 opacity-80" />
          <h3 className="text-sm font-bold text-gray-800">Sin logs de error en esta vista</h3>
          <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
            {statusFilter === 'unread'
              ? 'Todos los eventos de error han sido revisados y marcados como leídos.'
              : 'No se encontraron eventos coincidentes con los filtros seleccionados.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map((log) => {
            const badge = levelBadgeStyle[log.level] || levelBadgeStyle.INFO
            const isExpanded = expandedLogId === log.id

            return (
              <div
                key={log.id}
                className={`bg-white rounded-2xl border transition-all shadow-sm ${
                  log.isRead ? 'border-gray-200 opacity-80' : 'border-slate-300 ring-1 ring-slate-200'
                }`}
              >
                <div className="p-4 flex flex-col md:flex-row md:items-start justify-between gap-3">
                  <div className="space-y-1.5 flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className={`px-2 py-0.5 rounded-md font-extrabold border ${badge.bg} ${badge.text} ${badge.border}`}>
                        {badge.icon}
                        {log.level}
                      </span>

                      <span className="px-2 py-0.5 rounded-md font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                        {sourceLabels[log.source] || log.source}
                      </span>

                      {log.errorCode && (
                        <span className="font-mono text-gray-500 font-bold">
                          Cod: {log.errorCode}
                        </span>
                      )}

                      <span className="text-gray-400">
                        {new Date(log.createdAt).toLocaleString('es-ES')}
                      </span>

                      {log.isRead ? (
                        <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                          Leído {log.readBy ? `por ${log.readBy}` : ''}
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                          No leído
                        </span>
                      )}
                    </div>

                    <p className="text-xs font-semibold text-gray-900 break-words">
                      {log.message}
                    </p>

                    {log.tenantId && (
                      <div className="text-[11px] text-gray-500 font-mono">
                        Tenant: {log.tenantId}
                      </div>
                    )}
                  </div>

                  {/* Acciones */}
                  <div className="flex items-center gap-2 shrink-0 pt-2 md:pt-0">
                    {/* Botón para saltar al debugger si hay conversación */}
                    {(log.conversationId || log.traceId) && (
                      <Link
                        to={`/admin/debug?${log.conversationId ? `conversationId=${log.conversationId}` : `traceId=${log.traceId}`}${log.tenantId ? `&tenantId=${log.tenantId}` : ''}`}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-200 transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Ver traza
                      </Link>
                    )}

                    <button
                      onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                      className="px-2.5 py-1 text-xs font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
                    >
                      {isExpanded ? 'Ocultar payload' : 'Ver detalle'}
                    </button>

                    {!log.isRead && (
                      <button
                        onClick={() => void handleMarkAsRead(log.id)}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-lg shadow-xs transition-colors"
                      >
                        <Check className="w-3 h-3" />
                        Marcar leído
                      </button>
                    )}
                  </div>
                </div>

                {/* Detalle expandido */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold text-gray-500 uppercase">
                        Payload / Contexto Técnico
                      </span>
                      <button
                        onClick={() => handleCopy(log.id, JSON.stringify(log.payload || log, null, 2))}
                        className="text-[11px] font-semibold text-gray-600 hover:text-gray-900 flex items-center gap-1"
                      >
                        {copiedId === log.id ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                        {copiedId === log.id ? 'Copiado' : 'Copiar JSON'}
                      </button>
                    </div>

                    <pre className="text-[11px] font-mono bg-slate-900 text-slate-100 p-3 rounded-xl overflow-x-auto max-h-60 leading-relaxed">
                      {JSON.stringify(log.payload || { message: log.message, raw: log }, null, 2)}
                    </pre>

                    {log.stackTrace && (
                      <div>
                        <span className="text-[11px] font-bold text-red-600 uppercase">
                          Stack Trace
                        </span>
                        <pre className="text-[10px] font-mono bg-red-950 text-red-200 p-3 rounded-xl overflow-x-auto max-h-40 mt-1">
                          {log.stackTrace}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}

          {/* Paginación */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 text-xs font-bold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                ← Anterior
              </button>

              <span className="text-xs text-gray-600 font-medium">
                Página {page} de {pagination.totalPages}
              </span>

              <button
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-xs font-bold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Siguiente →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
